import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { match, P } from "ts-pattern";
import { API } from "../../../API";
import { AgencyId, CaregiverDocumentTypeId, ComplianceFormQuestion } from "../../../messages";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import { QueryStatus } from "../../../utils/reactQueryUtils";
import ErrorScaffold from "../../ErrorScaffold";
import AppLoadingPlaceholder from "../../Generic/AppLoadingPlaceholder";
import AppModal from "../../Generic/AppModal/AppModal";
import useModal from "../../useModal";
import { withMedflytAgencyId } from "../../withMedflytAgencyId";
import ComplianceFormScreen from "./ComplianceFormScreen";

interface Props {
  api: API;
  documentTypeId: CaregiverDocumentTypeId;
  medflytAgencyId: AgencyId;
  onClickGoBack: () => void;
}

function ComplianceFormActivity(props: Props) {
  const modal = useModal();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["copliance_form_document", props.documentTypeId, props.medflytAgencyId],
    queryFn: () => props.api.getComplianceFormFocument(props.medflytAgencyId, props.documentTypeId),
  });
  const mutatation = useMutation({
    mutationFn: (questions: ComplianceFormQuestion[]) =>
      props.api.createComplianceFormRecord(props.medflytAgencyId, {
        documentTypeId: props.documentTypeId,
        questions,
      }),
    onSuccess: () => {
      modal.setModalAsSuccess({
        title: translate(STRINGS.SUCCESS),
        message: translate(STRINGS.COMPLIANCE_FORM_SUBMITTED_SUCCESSFULLY),
        closeText: translate(STRINGS.CLOSE),
        onRequestClose: props.onClickGoBack,
      });
      queryClient.invalidateQueries(["compliance_items"]);
    },
    onError: () => {
      modal.setModalAsError({
        title: translate(STRINGS.ERROR),
        message: translate(STRINGS.SOMETHING_WENT_WRONG),
      });
    },
  });

  return (
    <>
      {match(query)
        .with(P.union(QueryStatus.Loading, QueryStatus.Idle), () => <AppLoadingPlaceholder />)
        .with(QueryStatus.Error, () => (
          <ErrorScaffold
            title={translate(STRINGS.COMPLIANCE_FORM)}
            message={translate(STRINGS.SOMETHING_WENT_WRONG)}
            onClickRetry={query.refetch}
            onClickGoBack={props.onClickGoBack}
          />
        ))
        .with(QueryStatus.ResolvedAs("data"), (result) => (
          <ComplianceFormScreen
            formDocument={result.data.formDocument}
            isSubmitting={mutatation.isLoading}
            onSubmit={mutatation.mutate}
            onClickGoBack={props.onClickGoBack}
            onError={modal.setModalAsError}
          />
        ))
        .exhaustive()}
      <AppModal {...modal.modalProps} />
    </>
  );
}

export default withMedflytAgencyId(ComplianceFormActivity);
